import { useState } from 'react';
import { useLang } from '../../../hooks/useLang';

const rewardAmounts = [6, 18, 36, 66, 88, 188];

interface RewardModalProps {
  open: boolean;
  onClose: () => void;
  articleTitle: string;
}

export default function RewardModal({ open, onClose, articleTitle }: RewardModalProps) {
  const { t } = useLang();
  const [selected, setSelected] = useState<number | null>(18);
  const [custom, setCustom] = useState('');
  const [step, setStep] = useState<'select' | 'confirm' | 'done'>('select');
  const [message, setMessage] = useState('');

  if (!open) return null;

  const finalAmount = custom ? Number(custom) : selected;

  const handleConfirm = () => {
    if (!finalAmount || finalAmount <= 0) return;
    setStep('confirm');
  };

  const handleDone = () => {
    setStep('done');
    setTimeout(() => {
      onClose();
      setStep('select');
      setCustom('');
      setSelected(18);
      setMessage('');
    }, 2500);
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center px-4" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      <div className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2E1065] to-[#4C1D95] px-6 py-5 text-white">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/60 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-award-line text-[#F59E0B] text-2xl"></i>
            </div>
            <h3 className="text-lg font-bold">{t('打赏支持', 'Support the Author')}</h3>
          </div>
          <p className="text-white/60 text-sm leading-relaxed line-clamp-2">《{articleTitle}》</p>
        </div>

        <div className="p-6">
          {step === 'select' && (
            <>
              <p className="text-sm text-gray-500 mb-4">
                {t('您的打赏是对清鉴元衡先生持续深度创作最直接的支持', 'Your reward directly supports Mr. ChaInsight\'s continued in-depth writing')}
              </p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {rewardAmounts.map(amt => (
                  <button
                    key={amt}
                    onClick={() => { setSelected(amt); setCustom(''); }}
                    className={`py-3 rounded-xl text-sm font-bold border-2 transition-all cursor-pointer whitespace-nowrap ${selected === amt && !custom ? 'border-[#7C3AED] bg-[#7C3AED]/5 text-[#7C3AED]' : 'border-gray-200 text-gray-700 hover:border-[#7C3AED]/50'}`}
                  >
                    ¥{amt}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm text-gray-500 shrink-0">{t('自定义', 'Custom')}：</span>
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">¥</span>
                  <input
                    type="number"
                    min="1"
                    placeholder={t('输入金额', 'Enter amount')}
                    value={custom}
                    onChange={e => { setCustom(e.target.value); setSelected(null); }}
                    className="w-full border border-gray-200 rounded-lg pl-7 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#7C3AED]"
                  />
                </div>
              </div>
              <textarea
                placeholder={t('留言给作者（选填）', 'Leave a message (optional)')}
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#7C3AED] mb-4"
              />
              <button
                onClick={handleConfirm}
                disabled={!finalAmount || finalAmount <= 0}
                className="w-full py-3 bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white font-bold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                {t(`确认打赏 ¥${finalAmount || 0}`, `Confirm ¥${finalAmount || 0}`)}
              </button>
            </>
          )}

          {step === 'confirm' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-qr-code-line text-[#7C3AED] text-3xl"></i>
              </div>
              <p className="text-gray-800 font-semibold mb-1">{t(`打赏金额：¥${finalAmount}`, `Amount: ¥${finalAmount}`)}</p>
              <p className="text-sm text-gray-500 mb-6">{t('请使用微信扫码完成打赏', 'Please scan QR code with WeChat to complete')}</p>
              <div className="w-40 h-40 bg-gray-100 rounded-xl mx-auto flex items-center justify-center border border-gray-200 mb-6">
                <div className="text-center">
                  <i className="ri-qr-code-line text-gray-400 text-4xl"></i>
                  <p className="text-xs text-gray-400 mt-2">{t('二维码演示', 'QR Demo')}</p>
                </div>
              </div>
              {message && (
                <div className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-2 mb-4 text-left">
                  <span className="text-gray-400">{t('您的留言：', 'Your message: ')}</span>{message}
                </div>
              )}
              <button onClick={handleDone} className="w-full py-3 bg-[#7C3AED] text-white font-bold rounded-xl hover:bg-[#6D28D9] transition-colors cursor-pointer">
                {t('已完成打赏', 'Reward Sent')}
              </button>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-fill text-green-500 text-3xl"></i>
              </div>
              <p className="text-gray-800 font-bold text-lg mb-2">{t('感谢您的支持！', 'Thank you for your support!')}</p>
              <p className="text-sm text-gray-500">{t('您的每一份打赏都是清鉴先生笔耕不辍的动力', 'Every reward fuels the author\'s continued writing')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}